import React, { useState, useEffect } from "react";
import { 
    Card, Table, Row, Col, Form, InputGroup, Button, Badge, 
    Dropdown, Spinner, Alert, Pagination
} from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { formatCurrency, formatNumber } from "../utils/formatters";
import { API_BASE_URL } from "../constants/api";


export default function Produits() {
    const navigate = useNavigate();
    const [produits, setProduits] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterText, setFilterText] = useState('');
    const [categorieFilter, setCategorieFilter] = useState('');
    const [stats, setStats] = useState(null);

    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);

    useEffect(() => {
        fetchProduits();
        fetchCategories();
        fetchStats();
    }, [categorieFilter]);

    const fetchProduits = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            let url = `${API_BASE_URL}/produits.php`;
            if (categorieFilter) {
                url += `?categorie=${categorieFilter}`;
            }
            
            const response = await fetch(url, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (!response.ok) throw new Error("Erreur chargement");
            
            const result = await response.json();
            setProduits(result.data || []);
            setCurrentPage(1);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchCategories = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/categories.php`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (response.ok) {
                const result = await response.json();
                setCategories(result.data || []);
            }
        } catch (err) {
            console.error("Erreur chargement catégories", err);
        }
    };

    const fetchStats = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/produits.php?action=stats`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (response.ok) {
                const result = await response.json();
                setStats(result.data);
            }
        } catch (err) {
            console.error("Erreur stats", err);
        }
    };

    const getStockStatus = (produit) => {
        if (!produit.stock_reel || produit.stock_reel <= 0) {
            return { label: 'Rupture', color: '#ef4444', icon: 'ri-close-circle-line' };
        }
        if (produit.stock_reel <= produit.seuil_alerte) {
            return { label: 'Critique', color: '#f59e0b', icon: 'ri-alert-line' };
        }
        return { label: 'Normal', color: '#10b981', icon: 'ri-check-line' };
    };

    const filteredProduits = produits.filter(p => 
        p.reference?.toLowerCase().includes(filterText.toLowerCase()) ||
        p.designation?.toLowerCase().includes(filterText.toLowerCase()) ||
        p.code_barre?.toLowerCase().includes(filterText.toLowerCase())
    );

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredProduits.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredProduits.length / itemsPerPage);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const renderPagination = () => {
        let items = [];
        const maxVisible = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
        let endPage = Math.min(totalPages, startPage + maxVisible - 1);

        if (endPage - startPage + 1 < maxVisible) {
            startPage = Math.max(1, endPage - maxVisible + 1);
        }

        if (startPage > 1) {
            items.push(
                <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
                    1
                </Pagination.Item>
            );
            if (startPage > 2) {
                items.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
            }
        }

        for (let number = startPage; number <= endPage; number++) {
            items.push(
                <Pagination.Item 
                    key={number} 
                    active={number === currentPage}
                    onClick={() => handlePageChange(number)}
                >
                    {number}
                </Pagination.Item>
            );
        }

        if (endPage < totalPages) {
            if (endPage < totalPages - 1) {
                items.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
            }
            items.push(
                <Pagination.Item key={totalPages} onClick={() => handlePageChange(totalPages)}>
                    {totalPages}
                </Pagination.Item>
            );
        }

        return items;
    };

    if (error) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <Alert variant="danger" className="border-0 shadow-sm rounded-3">
                        <div className="d-flex align-items-center">
                            <i className="ri-error-warning-line fs-1 me-3 text-danger"></i>
                            <div>
                                <h6 className="mb-1 fw-bold">Erreur de chargement</h6>
                                <p className="mb-0 small">{error}</p>
                                <Button variant="outline-danger" size="sm" className="mt-2 rounded-pill" onClick={fetchProduits}>
                                    <i className="ri-refresh-line me-1"></i>Réessayer
                                </Button>
                            </div>
                        </div>
                    </Alert>
                </div>
                <Footer />
            </>
        );
    }

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                <div className="container-fluid">
                    {/* En-tête simplifié */}
                    <div className="d-md-flex align-items-center justify-content-between mb-4">
                        <div>
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <div className="p-2 bg-primary bg-opacity-10 rounded-3">
                                    <i className="ri-suitcase-2-line fs-20 text-primary" style={{ color: '#21ACCD' }}></i>
                                </div>
                                <div>
                                    <h4 className="main-title mb-0 fw-bold">Gestion des produits</h4>
                                    <p className="text-muted small mb-0">Gérez votre catalogue de produits</p>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 mt-3 mt-md-0">
                            <Button 
                                variant="primary" 
                                onClick={() => navigate('/stock/produitsAjouter')}
                                className="rounded-pill px-3 d-flex align-items-center gap-1"
                                size="sm"
                                style={{ background: '#21ACCD', border: 'none' }}
                            >
                                <i className="ri-add-line"></i>
                                Nouveau produit
                            </Button>
                            <Button 
                                variant="outline-primary" 
                                onClick={() => navigate('/stock/produits/import')}
                                className="rounded-pill px-3 d-flex align-items-center gap-1"
                                size="sm"
                                style={{ borderColor: '#21ACCD', color: '#21ACCD' }}
                            >
                                <i className="ri-upload-cloud-2-line"></i>
                                Importer
                            </Button>
                        </div>
                    </div>

                    {/* KPI Cards modernes */}
                    {stats && (
                        <Row className="g-3 mb-4">
                            <Col sm={6} xl={3}>
                                <div className="kpi-card kpi-primary">
                                    <div className="kpi-icon">
                                        <i className="ri-suitcase-2-line fs-24"></i>
                                    </div>
                                    <div className="kpi-content">
                                        <span className="kpi-label">Total produits</span>
                                        <h3 className="kpi-value">{stats.total || 0}</h3>
                                        <span className="kpi-unit">en stock</span>
                                    </div>
                                </div>
                            </Col>
                            <Col sm={6} xl={3}>
                                <div className="kpi-card kpi-danger">
                                    <div className="kpi-icon">
                                        <i className="ri-close-circle-line fs-24"></i>
                                    </div>
                                    <div className="kpi-content">
                                        <span className="kpi-label">Rupture</span>
                                        <h3 className="kpi-value">{stats.rupture || 0}</h3>
                                        <span className="kpi-unit">à réapprovisionner</span>
                                    </div>
                                </div>
                            </Col>
                            <Col sm={6} xl={3}>
                                <div className="kpi-card kpi-warning">
                                    <div className="kpi-icon">
                                        <i className="ri-alert-line fs-24"></i>
                                    </div>
                                    <div className="kpi-content">
                                        <span className="kpi-label">Stock critique</span>
                                        <h3 className="kpi-value">{stats.alerte || 0}</h3>
                                        <span className="kpi-unit">seuil bas</span>
                                    </div>
                                </div>
                            </Col>
                            <Col sm={6} xl={3}>
                                <div className="kpi-card kpi-success">
                                    <div className="kpi-icon">
                                        <i className="ri-money-euro-circle-line fs-24"></i>
                                    </div>
                                    <div className="kpi-content">
                                        <span className="kpi-label">Valeur stock</span>
                                        <h3 className="kpi-value" style={{ fontSize: '1rem' }}>{formatCurrency(stats.valeur?.valeur_achat || 0)}</h3>
                                        <span className="kpi-unit">prix d'achat</span>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    )}

                    {/* Filtres modernes */}
                    <Card className="border-0 shadow-sm rounded-3 mb-4">
                        <Card.Body className="py-3 px-4">
                            <Row className="align-items-center g-3">
                                <Col md={5}>
                                    <div className="position-relative">
                                        <i className="ri-search-line position-absolute top-50 start-0 translate-middle-y ms-3 text-muted fs-14"></i>
                                        <Form.Control
                                            type="text"
                                            placeholder="Rechercher référence, désignation..."
                                            value={filterText}
                                            onChange={(e) => setFilterText(e.target.value)}
                                            className="ps-5 rounded-pill"
                                        />
                                    </div>
                                </Col>
                                <Col md={4}>
                                    <Form.Select 
                                        value={categorieFilter}
                                        onChange={(e) => setCategorieFilter(e.target.value)}
                                        className="rounded-pill"
                                    >
                                        <option value="">Toutes catégories</option>
                                        {categories.map(cat => (
                                            <option key={cat.id_categorie} value={cat.id_categorie}>
                                                {cat.nom_categorie}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Col>
                                <Col md={3} className="text-end">
                                    <span className="badge bg-light text-dark rounded-pill px-3 py-2">
                                        <i className="ri-stack-line me-1"></i>
                                        {filteredProduits.length} produit(s)
                                    </span>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Liste des produits avec pagination */}
                    <Card className="border-0 shadow-sm rounded-3 overflow-hidden">
                        <Card.Body className="p-0">
                            <div className="table-responsive">
                                <Table hover className="align-middle mb-0 custom-table">
                                    <thead className="bg-light">
                                        <tr>
                                            <th className="ps-4">Référence</th>
                                            <th>Désignation</th>
                                            <th>Catégorie</th>
                                            <th className="text-end">Prix vente</th>
                                            <th className="text-center">Stock</th>
                                            <th>Statut</th>
                                            <th className="text-center pe-4">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loading ? (
                                            <tr>
                                                <td colSpan="7" className="text-center py-5">
                                                    <Spinner animation="border" variant="primary" style={{ color: '#21ACCD' }} />
                                              </td>
                                            </tr>
                                        ) : currentItems.length > 0 ? (
                                            currentItems.map((produit) => {
                                                const stockStatus = getStockStatus(produit);
                                                return (
                                                    <tr key={produit.id_produit} className="produit-row">
                                                        <td className="ps-4">
                                                            <span className="reference-badge">
                                                                <i className="ri-barcode-line me-1 fs-10"></i>
                                                                {produit.reference}
                                                            </span>
                                                            {produit.code_barre && (
                                                                <small className="text-muted d-block mt-1">{produit.code_barre}</small>
                                                            )}
                                                     </td>
                                                        <td className="fw-semibold" style={{minWidth:'300px'}}>{produit.designation}</td>
                                                        <td>{produit.nom_categorie || '-'}</td>
                                                        <td className="text-end fw-bold text-primary">{formatCurrency(produit.prix_vente_ht)}</td>
                                                        <td className="text-center">
                                                            <span className={`fw-bold ${stockStatus.color === '#ef4444' ? 'text-danger' : stockStatus.color === '#f59e0b' ? 'text-warning' : 'text-success'}`}>
                                                                {formatNumber(produit.stock_reel || 0)}
                                                            </span>
                                                            {produit.stock_reel <= produit.seuil_alerte && produit.stock_reel > 0 && (
                                                                <i className="ri-alert-line text-warning ms-1" title="Stock critique"></i>
                                                            )}
                                                            {(!produit.stock_reel || produit.stock_reel <= 0) && (
                                                                <i className="ri-close-circle-line text-danger ms-1" title="Rupture de stock"></i>
                                                            )}
                                                   </td>
                                                        <td>
                                                            <span className="status-badge" style={{ background: `${stockStatus.color}15`, color: stockStatus.color }}>
                                                                <i className={`${stockStatus.icon} me-1 fs-10`}></i>
                                                                {stockStatus.label}
                                                          
                                                        </span></td>
                                                        <td className="text-center pe-4">
                                                            <div className="d-flex gap-2 justify-content-center">
                                                                <Button 
                                                                    variant="outline-info" 
                                                                    size="sm"
                                                                    className="action-btn"
                                                                    onClick={() => navigate(`/stock/produits/${produit.id_produit}`)}
                                                                >
                                                                    <i className="ri-eye-line"></i>
                                                                </Button>
                                                                <Button 
                                                                    variant="outline-primary" 
                                                                    size="sm"
                                                                    className="action-btn"
                                                                    onClick={() => navigate(`/stock/produits/modifier/${produit.id_produit}`)}
                                                                >
                                                                    <i className="ri-pencil-line"></i>
                                                                </Button>
                                                                <Button 
                                                                    variant="outline-success" 
                                                                    size="sm"
                                                                    className="action-btn"
                                                                    onClick={() => navigate(`/stock/mouvements/ajouter/${produit.id_produit}`)}
                                                                    title="Ajouter au stock"
                                                                >
                                                                    <i className="ri-add-box-line"></i>
                                                                </Button>
                                                            </div>
                                                  </td>
                                                    </tr>
                                                );
                                            })
                                        ) : (
                                            <tr>
                                                <td colSpan="7" className="text-center py-5">
                                                    <div className="empty-state">
                                                        <i className="ri-suitcase-2-line empty-icon text-primary" style={{ fontSize: '4rem' }}></i>
                                                        <h5 className="mt-3">Aucun produit trouvé</h5>
                                                        <p className="text-muted mb-3">
                                                            {filterText || categorieFilter 
                                                                ? "Aucun résultat ne correspond à vos critères"
                                                                : "Commencez par ajouter votre premier produit"}
                                                        </p>
                                                        {(filterText || categorieFilter) ? (
                                                            <Button 
                                                                variant="outline-primary"
                                                                onClick={() => {
                                                                    setFilterText('');
                                                                    setCategorieFilter('');
                                                                }}
                                                                className="rounded-pill"
                                                            >
                                                                <i className="ri-refresh-line me-2"></i>Réinitialiser
                                                            </Button>
                                                        ) : (
                                                            <Button 
                                                                variant="primary"
                                                                onClick={() => navigate('/stock/produits/ajouter')}
                                                                className="rounded-pill"
                                                                style={{ background: '#21ACCD', border: 'none' }}
                                                            >
                                                                <i className="ri-add-line me-2"></i>Ajouter un produit
                                                            </Button>
                                                        )}
                                                    </div>
                                            </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div className="d-flex justify-content-between align-items-center p-3 border-top">
                                    <div className="text-muted small">
                                        <i className="ri-file-list-line me-1"></i>
                                        Affichage de {indexOfFirstItem + 1} à {Math.min(indexOfLastItem, filteredProduits.length)} sur {filteredProduits.length} produits
                                    </div>
                                    <Pagination className="mb-0">
                                        <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
                                        {renderPagination()}
                                        <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} />
                                    </Pagination>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </div>
                <Footer />
            </div>

            <style>{`
                /* KPI Cards */
                .kpi-card {
                    background: white;
                    border-radius: 0px;
                    padding: 1rem;
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
                    transition: all 0.2s ease;
                    height: 100%;
                    border-left: 2px solid;
                }
                .kpi-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 20px rgba(0,0,0,0.08);
                }
                .kpi-primary { border-left-color: #21ACCD; }
                .kpi-danger { border-left-color: #ef4444; }
                .kpi-warning { border-left-color: #f59e0b; }
                .kpi-success { border-left-color: #10b981; }
                
                .kpi-icon {
                    width: 48px;
                    height: 48px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                .kpi-primary .kpi-icon { background: #e2f3f5; color: #21ACCD; }
                .kpi-danger .kpi-icon { background: #fef2f2; color: #ef4444; }
                .kpi-warning .kpi-icon { background: #fffbeb; color: #f59e0b; }
                .kpi-success .kpi-icon { background: #ecfdf5; color: #10b981; }
                
                .kpi-content {
                    flex: 1;
                }
                .kpi-label {
                    font-size: 0.7rem;
                    text-transform: uppercase;
                    color: #6c757d;
                    font-weight: 600;
                    letter-spacing: 0.5px;
                }
                .kpi-value {
                    font-size: 1.3rem;
                    font-weight: 700;
                    margin: 0.25rem 0;
                    color: #1a3c5e;
                }
                .kpi-unit {
                    font-size: 0.7rem;
                    color: #6c757d;
                }
                
                /* Reference Badge */
                .reference-badge {
                    background: #f1f5f9;
                    padding: 0.2rem 0.6rem;
                    border-radius: 6px;
                    font-size: 0.7rem;
                    font-family: monospace;
                    display: inline-block;
                }
                
                /* Status Badge */
                .status-badge {
                    display: inline-flex;
                    align-items: center;
                    padding: 0.3rem 0.8rem;
                    border-radius: 30px;
                    font-size: 0.7rem;
                    font-weight: 500;
                }
                
                /* Action Buttons */
                .action-btn {
                    width: 32px;
                    height: 32px;
                    border-radius: 8px;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.2s ease;
                    border: 1px solid #e2e8f0;
                    background: white;
                    color: #64748b;
                }
                .action-btn:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
                }
                .btn-outline-info:hover {
                    background: linear-gradient(135deg, #0891b2, #0e7490);
                    border-color: #0891b2;
                    color: white;
                }
                .btn-outline-primary:hover {
                    background: linear-gradient(135deg, #21ACCD, #1a8ba8);
                    border-color: #21ACCD;
                    color: white;
                }
                .btn-outline-success:hover {
                    background: linear-gradient(135deg, #10b981, #059669);
                    border-color: #10b981;
                    color: white;
                }
                
                /* Custom Table */
                .custom-table thead th {
                    background: #f8fafc;
                    color: #1e293b;
                    font-weight: 600;
                    font-size: 0.75rem;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    padding: 1rem 0.75rem;
                    border-bottom: 2px solid #e2e8f0;
                }
                
                .custom-table tbody td {
                    padding: 0.75rem;
                    vertical-align: middle;
                    border-bottom: 1px solid #f1f5f9;
                }
                
                .produit-row {
                    transition: all 0.2s ease;
                }
                
                .produit-row:hover {
                    background: #f8fafc;
                }
                
                /* Pagination */
                .pagination {
                    gap: 5px;
                }
                .pagination .page-item .page-link {
                    border-radius: 8px;
                    border: 1px solid #e2e8f0;
                    color: #1e293b;
                    padding: 0.5rem 1rem;
                    transition: all 0.2s ease;
                    font-weight: 500;
                }
                .pagination .page-item.active .page-link {
                    background: linear-gradient(135deg, #21ACCD, #1a8ba8);
                    border-color: #21ACCD;
                    color: white;
                    box-shadow: 0 4px 10px rgba(33, 172, 205, 0.3);
                }
                .pagination .page-item .page-link:hover {
                    background: #f1f5f9;
                    transform: translateY(-2px);
                    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
                }
                .pagination .page-item.disabled .page-link {
                    opacity: 0.5;
                    pointer-events: none;
                }
                
                .rounded-3 {
                    border-radius: 12px !important;
                }
                
                .rounded-pill {
                    border-radius: 50px !important;
                }
                
                .fs-10 {
                    font-size: 0.625rem;
                }
                .fs-14 {
                    font-size: 0.875rem;
                }
                .fs-20 {
                    font-size: 1.25rem;
                }
                .fs-24 {
                    font-size: 1.5rem;
                }
            `}</style>
        </>
    );
}